import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Brush,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  downloadTexcumarExport,
  getTexcumarAlerts,
  getTexcumarFleet,
  getTexcumarRooms,
  getTexcumarSeries,
  getTexcumarStats,
  getTexcumarTelemetry,
  getTexcumarTrends,
} from '../../utils/api';
import {
  DEFAULT_FRESHNESS,
  bucketLabel,
  clampRange,
  dayValue,
  endOfDay,
  formatDateTime,
  formatDuration,
  formatTimeAxis,
  freshness,
  rangeForDays,
  rangeForHourWindow,
  rangeForHours,
  rangeLabel,
  relativeTime,
  startOfDay,
} from './lib/dates';
import {
  baseLabel,
  formatCount,
  formatValue,
  roomLabel,
  roomName,
  roomOptionLabel,
  roomShortName,
} from './lib/format';
import {
  BANDAS_CULTIVO,
  STATUS_LABEL,
  bandasDe,
  byCriticality,
  isFleetView,
  isProblem,
  roomForPresentation,
  roomsInScope,
  statusFor,
  summarizeBases,
} from './lib/status';
import { buildEvolution, limitEvolutionSeries } from './lib/evolution';
import { TexcumarConfigPanel } from './ConfigPanel';
import { MantenimientoPanel } from './MantenimientoPanel';

const PAGE_SIZES = [25, 50, 100];

/* El servidor envía las bandas resueltas por serie en cada fila. Este respaldo
 * cubre la tabla de detalle y las series que el catálogo todavía no conoce. */

const METRICS = [
  { key: 'temperatura_do', label: 'Temperatura', short: 'Temperatura', unit: '°C', threshold: 'temperature', color: '#f97316', countKey: 'do_measurements' },
  { key: 'oxigeno_mgl', label: 'Oxígeno disuelto', short: 'Oxígeno', unit: 'mg/L', threshold: 'oxygen', color: '#06b6d4', countKey: 'do_measurements' },
  { key: 'saturacion_pct', label: 'Saturación', short: 'Saturación', unit: '%', threshold: 'saturation', color: '#8b5cf6', countKey: 'do_measurements' },
  { key: 'sensor_temperature', label: 'Agua de mar', short: 'Agua de mar', unit: '°C', threshold: 'temperature', color: '#3b82f6', countKey: 'sensor_temperature_measurements' },
  { key: 'salinity_ppt', label: 'Salinidad', short: 'Salinidad', unit: 'ppt', threshold: null, color: '#10b981', countKey: 'salinity_measurements' },
];

/* Atajos de rango. Cada uno se identifica por `id` y no por sus fechas: una
 * ventana móvil como «últimas 24 h» cambia en cada milisegundo, así que
 * compararla por fechas exactas dejaría el botón apagado en cuanto se
 * renderiza. Los tramos horarios son los que interesan en una camaronera:
 * la madrugada es cuando el oxígeno toca fondo por respiración nocturna. */
const QUICK_RANGES = [
  { id: 'today', label: 'Hoy', range: () => rangeForDays(1) },
  { id: 'last24h', label: '24 h', hint: 'Últimas 24 horas', range: () => rangeForHours(24) },
  { id: 'dawn', label: 'Madrugada', hint: '00:00 a 06:00', range: () => rangeForHourWindow(0, 6) },
  { id: 'night', label: 'Turno noche', hint: '18:00 a 06:00', range: () => rangeForHourWindow(18, 6) },
  { id: 'last7d', label: '7 días', range: () => rangeForDays(7) },
  { id: 'last30d', label: '30 días', range: () => rangeForDays(30) },
];

/* Filtro por estado. Es la entrada natural a la pantalla cuando hay muchas
 * salas: lo primero que se pregunta no es «cómo está todo» sino «qué está mal». */
const STATUS_FILTERS = [
  { id: 'all', label: 'Todas' },
  { id: 'problems', label: 'Solo problemas' },
  { id: 'critical', label: 'Críticas' },
  { id: 'warning', label: 'Atención' },
  { id: 'sin_datos', label: 'Sin datos' },
  { id: 'mantenimiento', label: 'En mantenimiento' },
  { id: 'ok', label: 'Óptimas' },
];

// Métricas que la cabecera de flota resume cuando no hay una sala concreta
// seleccionada. El oxígeno va primero porque es el que mata.

// A partir de cuántas salas la grilla de tarjetas deja de ser legible y conviene
// la tabla-ranking. Con pocas salas las tarjetas dan más contexto de un vistazo.
/* Cuántas salas se muestran en tarjeta grande antes de pasar al mosaico.
 * Seis caben en una fila y siguen leyéndose de lejos; a partir de ahí, una
 * pared de tarjetas idénticas deja de señalar nada. */
const ROOM_CARD_LIMIT = 6;

/* Cuántas series dibuja como máximo el gráfico de evolución.
 *
 * Es un límite de legibilidad antes que de rendimiento: una línea por serie y
 * ocho colores en la paleta, así que por encima de una docena las líneas se
 * repiten de color y la leyenda ocupa más que el propio gráfico. Con 100 salas
 * llegan 14.400 puntos en 100 series y el lienzo deja de decir nada.
 *
 * Se dibujan las más críticas y se dice cuántas quedaron fuera: un recorte
 * silencioso se leería como «esto es todo lo que hay». */
const MAX_CHART_SERIES = 12;

/* --------------------------------------------------------------------- */
/* Filtros reactivos                                                      */
/* --------------------------------------------------------------------- */

// Los filtros se aplican al cambiarlos, sin botón. Un input de fecha emite un
// onChange por cada dígito tecleado, así que la consulta se retrasa un poco
// para no disparar peticiones con fechas a medio escribir.
const FILTER_DEBOUNCE_MS = 350;

function useDebouncedValue(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

// El catálogo de salas cambia muy rara vez: se pide una sola vez por sesión de
// navegador en lugar de en cada montaje de la página. Un fallo descarta la
// promesa para que el siguiente montaje reintente.
//
// Los umbrales ya no se piden aparte: viajan resueltos por serie dentro de la
// respuesta de /fleet, porque cada sala puede tener los suyos.
let roomsPromise = null;

function loadRooms() {
  if (!roomsPromise) {
    roomsPromise = getTexcumarRooms().catch((err) => { roomsPromise = null; throw err; });
  }
  return roomsPromise;
}

/* --------------------------------------------------------------------- */
/* Componentes de presentación                                            */
/* --------------------------------------------------------------------- */

/* --------------------------------------------------------------------- */
/* Panel plegable                                                         */
/*                                                                        */
/* Cada bloque se puede cerrar y el estado sobrevive a la recarga: quien   */
/* solo mira las alertas no tiene que pasar cada mañana por encima del     */
/* gráfico y de la tabla.                                                  */
/*                                                                        */
/* El estado va en `localStorage` y no en el servidor porque es preferencia*/
/* de la persona y del navegador, no de la operación. Si el navegador lo   */
/* tiene bloqueado —modo privado, política de empresa— el panel abre y ya. */
/* --------------------------------------------------------------------- */

function usePanelAbierto(id, porDefecto) {
  const clave = `tx-panel-${id}`;
  const [abierto, setAbierto] = useState(() => {
    try {
      const guardado = window.localStorage.getItem(clave);
      return guardado === null ? porDefecto : guardado === '1';
    } catch (err) {
      return porDefecto;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(clave, abierto ? '1' : '0');
    } catch (err) {
      // Sin almacenamiento, el panel simplemente no recuerda.
    }
  }, [clave, abierto]);

  return [abierto, setAbierto];
}

const Panel = React.forwardRef(function Panel(
  { id, titulo, resumen, acciones, className = '', porDefecto = true, children },
  ref
) {
  const [abierto, setAbierto] = usePanelAbierto(id, porDefecto);

  return (
    <section className={`tx-panel ${className} ${abierto ? 'is-open' : 'is-closed'}`} ref={ref}>
      <div className="tx-panel-heading">
        <button
          type="button"
          className="tx-panel-toggle"
          aria-expanded={abierto}
          onClick={() => setAbierto((valor) => !valor)}
        >
          <span className="tx-panel-caret" aria-hidden="true" />
          <span className="tx-panel-titles">
            <h3>{titulo}</h3>
            {resumen && <span>{resumen}</span>}
          </span>
        </button>
        {/* Las acciones solo tienen sentido con el panel abierto: un botón de
            «ver todas» sobre un panel cerrado no lleva a ninguna parte. */}
        {abierto && acciones}
      </div>
      {abierto && children}
    </section>
  );
});

function StatusChip({ status, children }) {
  return <span className={`tx-chip tx-chip-${status}`}>{children || STATUS_LABEL[status]}</span>;
}

function KpiCard({ metric, stats, range, threshold, onFocus }) {
  const current = stats?.[`current_${metric.key}`];
  const average = stats?.[`avg_${metric.key}`];
  const status = statusFor(current, threshold);
  return (
    <article className={`tx-kpi tx-kpi-${status}`}>
      <header>
        <span className="tx-kpi-label">{metric.label}</span>
        <StatusChip status={status} />
      </header>
      <strong className="tx-kpi-value">
        {formatValue(current)}
        <small>{metric.unit}</small>
      </strong>
      <dl className="tx-kpi-range">
        <div><dt>Mín</dt><dd>{formatValue(range?.min)}</dd></div>
        {/* El promedio está ponderado por número de lecturas, no por tiempo ni
            por sala: se dice para que nadie lo lea como un promedio de flota. */}
        <div>
          <dt title="Promedio ponderado por número de lecturas del rango">Prom</dt>
          <dd>{formatValue(average)}</dd>
        </div>
        <div><dt>Máx</dt><dd>{formatValue(range?.max)}</dd></div>
      </dl>
      {threshold && (
        <p className="tx-kpi-threshold">
          Rango ideal {formatValue(threshold.idealMin, 1)}–{formatValue(threshold.idealMax, 1)} {metric.unit}
        </p>
      )}
      <button type="button" className="tx-link-button" onClick={() => onFocus(metric.key)}>
        Ver evolución
      </button>
    </article>
  );
}

/* --------------------------------------------------------------------- */
/* Cabecera de operación                                                  */
/*                                                                        */
/* Sustituye al bloque de la nube y a las tres tarjetas de base. Responde  */
/* de un vistazo lo único que se pregunta al abrir la pantalla: si algo    */
/* está mal, qué es, y si los datos siguen llegando.                       */
/*                                                                        */
/* Las cuatro cifras ya venían calculadas en /fleet —`counts`, `worst`,    */
/* `risk` y `stale_rooms`— y la página las descartaba: de toda la          */
/* respuesta solo usaba el número de salas. Eran cuatro resúmenes          */
/* viajando por la red para no enseñarse.                                  */
/*                                                                        */
/* La saturación no aparece: es el mg/L en otra unidad, y tener dos formas */
/* de decir lo mismo en la vista general obliga a explicar cuál manda.     */
/* --------------------------------------------------------------------- */

function StatBlock({ label, value, unit, detail, status = 'neutral', onClick }) {
  const contenido = (
    <>
      <span className="tx-stat-label">{label}</span>
      <strong className="tx-stat-value">
        {value}
        {unit && <small>{unit}</small>}
      </strong>
      {detail && <span className="tx-stat-detail">{detail}</span>}
    </>
  );

  if (!onClick) return <div className={`tx-stat tx-stat-${status}`}>{contenido}</div>;
  return (
    <button type="button" className={`tx-stat tx-stat-${status} is-clickable`} onClick={onClick}>
      {contenido}
    </button>
  );
}

function FleetSummary({ fleet, bases, activeBase, onSelectBase, onFocusRoom, onFilterStatus }) {
  const counts = fleet?.counts;
  if (!counts || !counts.total) return null;

  const atendibles = (counts.critical || 0) + (counts.warning || 0);
  const sinDatos = counts.sin_datos || 0;
  const optimas = counts.ok || 0;
  const worstTemp = fleet.worst ? fleet.worst.temperatura_do : null;
  const worstOx = fleet.worst ? fleet.worst.oxigeno_mgl : null;
  const stale = fleet.stale_rooms || [];
  const risk = fleet.risk || {};
  /* Se enseña la sala que más tiempo estuvo fuera, no la suma de todas: en una
   * ventana de 24 h, veintiuna salas suman cifras de tres dígitos que se leen
   * como un error. */
  const peorRacha = [risk.temperature, risk.oxygen]
    .filter(Boolean)
    .reduce((acc, bloque) => (
      (bloque.worst_minutes || 0) > (acc ? acc.worst_minutes || 0 : 0) ? bloque : acc
    ), null);

  // Los tramos de la barra van en el orden en que importan, no alfabético: lo
  // que hay que atender primero y a la izquierda, que es donde se mira.
  const tramos = [
    { key: 'critical', valor: counts.critical || 0 },
    { key: 'warning', valor: counts.warning || 0 },
    { key: 'sin_datos', valor: sinDatos },
    { key: 'ok', valor: optimas },
  ].filter((tramo) => tramo.valor > 0);

  return (
    <section className="tx-summary" aria-label="Estado general de la operación">
      <div className="tx-summary-main">
        <div className="tx-summary-headline">
          <strong className={atendibles || sinDatos ? 'is-alerta' : 'is-ok'}>
            {optimas}<em>/{counts.total}</em>
          </strong>
          <span>{counts.total === 1 ? 'sala en rango' : 'salas en rango'}</span>
        </div>

        <div className="tx-summary-bar" role="img" aria-label={`${optimas} de ${counts.total} salas en rango`}>
          {tramos.map((tramo) => (
            <i
              key={tramo.key}
              className={`tx-bar-${tramo.key}`}
              style={{ flexGrow: tramo.valor }}
              title={`${tramo.valor} · ${STATUS_LABEL[tramo.key]}`}
            />
          ))}
        </div>

        <div className="tx-summary-tx">
          <span>Última transmisión</span>
          <strong>{relativeTime(fleet.latest_at)}</strong>
          <em>{fleet.latest_at ? formatDateTime(fleet.latest_at) : 'sin lecturas en el rango'}</em>
        </div>
      </div>

      {/* El aviso de salas mudas va primero y entero: una sala que no reporta no
          sale marcada por sus valores sino por su ausencia, y es el modo de
          fallo que más fácil pasa desapercibido. */}
      {stale.length > 0 && (
        <button type="button" className="tx-summary-stale" onClick={() => onFilterStatus('sin_datos')}>
          <span className="tx-stale-dot" />
          <strong>
            {stale.length === 1 ? '1 sala sin recibir datos' : `${stale.length} salas sin recibir datos`}
          </strong>
          <span className="tx-stale-list">
            {stale.slice(0, 3).map((room) => (
              <em key={room.serie}>
                {baseLabel(room.base_code)} · {room.nombre_completo}
                <i>
                  {room.stale_minutes === null || room.stale_minutes === undefined
                    ? 'nunca reportó'
                    : formatDuration(room.stale_minutes)}
                </i>
              </em>
            ))}
            {stale.length > 3 && <em>y {stale.length - 3} más</em>}
          </span>
        </button>
      )}

      <div className="tx-summary-stats">
        <StatBlock
          label={atendibles === 1 ? 'Requiere atención' : 'Requieren atención'}
          value={atendibles}
          status={atendibles ? (counts.critical ? 'critical' : 'warning') : 'ok'}
          detail={atendibles ? 'ver solo estas' : 'todo en banda'}
          onClick={atendibles ? () => onFilterStatus('problems') : undefined}
        />
        {worstTemp && (
          <StatBlock
            label="Temperatura más desviada"
            value={formatValue(worstTemp.value, 1)}
            unit="°C"
            status={worstTemp.status}
            detail={`${baseLabel(worstTemp.base_code)} · ${worstTemp.nombre_completo}`}
            onClick={() => onFocusRoom(worstTemp)}
          />
        )}
        {worstOx && (
          <StatBlock
            label="Oxígeno más bajo"
            value={formatValue(worstOx.value, 1)}
            unit="mg/L"
            status={worstOx.status}
            detail={`${baseLabel(worstOx.base_code)} · ${worstOx.nombre_completo}`}
            onClick={() => onFocusRoom(worstOx)}
          />
        )}
        {/* Tiempo fuera de banda: solo cuando lo hubo. Un «0 min» permanente
            ocupa el mismo sitio que una cifra que sí dice algo. */}
        {peorRacha && peorRacha.worst_minutes > 0 && (
          <StatBlock
            label="Más tiempo fuera de banda"
            value={formatDuration(peorRacha.worst_minutes)}
            status="warning"
            detail={`${peorRacha.worst_room} · ${peorRacha.rooms === 1 ? '1 sala afectada' : `${peorRacha.rooms} salas afectadas`}`}
          />
        )}
      </div>

      {bases.length > 1 && (
        <div className="tx-summary-bases" role="group" aria-label="Bases remotas">
          <button
            type="button"
            className={`tx-base-chip ${activeBase === 'all' ? 'is-active' : ''}`}
            onClick={() => onSelectBase('all')}
          >
            Todas
          </button>
          {bases.map((base) => (
            <button
              key={base.code}
              type="button"
              className={`tx-base-chip tx-base-${base.status} ${activeBase === base.code ? 'is-active' : ''}`}
              onClick={() => onSelectBase(base.code)}
            >
              {baseLabel(base.code)}
              <em>{base.active}/{base.rooms}</em>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

/* --------------------------------------------------------------------- */
/* Azulejo de sala                                                        */
/*                                                                        */
/* Densidad inversa a la normalidad: lo que hay que atender se queda en    */
/* tarjeta grande y lo que está en rango se encoge a esto. Antes todo se   */
/* dibujaba igual, así que veintiuna salas sanas ocupaban tres pantallas   */
/* y escondían a la única que estaba mal.                                  */
/* --------------------------------------------------------------------- */

function RoomTile({ room, onSelect }) {
  const bands = bandasDe(room);
  const temperatura = room.current_temperatura_do;
  const oxigeno = room.current_oxigeno_mgl;

  return (
    <button
      type="button"
      className={`tx-tile tx-tile-${room.status}`}
      onClick={() => onSelect(room)}
      title={`${room.nombre_completo} · ${baseLabel(room.base_code)}`}
    >
      <span className="tx-tile-base">{baseLabel(room.base_code)}</span>
      <span className="tx-tile-name">{roomShortName(room)}</span>
      <strong className={`tx-tile-value tx-cell-${statusFor(temperatura, bands.temperature)}`}>
        {formatValue(temperatura, 1)}<small>°C</small>
      </strong>
      <span className={`tx-tile-second tx-cell-${statusFor(oxigeno, bands.oxygen)}`}>
        {formatValue(oxigeno, 1)}<small>mg/L</small>
      </span>
    </button>
  );
}

/* --------------------------------------------------------------------- */
/* Bandeja de alertas                                                     */
/*                                                                        */
/* Los eventos que el propio equipo marcó como ALERT. Llevaban semanas     */
/* guardándose y solo asomaban como una columna de la tabla de detalle.    */
/* Cada fila lleva su hora, su temperatura y su oxígeno, con la magnitud   */
/* desviada resaltada, para que se entienda sin abrir la sala.             */
/* --------------------------------------------------------------------- */

function AlertsTray({ alerts, loading, onSelectRoom }) {
  const todos = (alerts && alerts.data) || [];

  /* Solo se listan los avisos que desvían temperatura u oxígeno. El resto
   * —que en un día normal son la inmensa mayoría— son disparos del propio
   * equipo por criterios que esta pantalla no muestra: listarlos uno a uno
   * llenaba el panel de ochenta filas idénticas que no piden nada. */
  const eventos = todos.filter((evento) => evento.motivo !== 'aviso del equipo');
  const rutinarios = (alerts ? alerts.total || 0 : 0) - (alerts ? alerts.total_desviadas || 0 : 0);

  return (
    <Panel
      id="alertas"
      className="tx-alerts"
      titulo="Alertas del equipo"
      resumen={loading
        ? 'Consultando…'
        : eventos.length
          ? `${formatCount(eventos.length)} con desviación de temperatura u oxígeno`
          : 'Ninguna con desviación de temperatura ni oxígeno'}
      acciones={alerts && alerts.last_transmission && (
        <div className="tx-alerts-last">
          <span>Última transmisión</span>
          <strong>{formatDateTime(alerts.last_transmission)}</strong>
        </div>
      )}
    >

      {!loading && !eventos.length ? (
        <div className="tx-empty">
          {alerts && alerts.total
            ? 'Ninguna sala salió de banda. Los avisos del rango son del propio equipo.'
            : 'Ninguna sala disparó una alerta en el rango seleccionado.'}
        </div>
      ) : (
        <ul className="tx-alert-list">
          {eventos.map((evento) => (
            <li key={evento.id} className={`tx-alert tx-alert-${evento.severidad}`}>
              <button type="button" onClick={() => onSelectRoom(evento)}>
                <span className="tx-alert-when">
                  <strong>{formatDateTime(evento.telemetry_time)}</strong>
                  <em>{relativeTime(evento.telemetry_time)}</em>
                </span>
                <span className="tx-alert-room">
                  <em>{baseLabel(evento.base_code)}</em>
                  <strong>{evento.nombre_completo}</strong>
                </span>
                <span className={`tx-alert-metric tx-cell-${evento.temperatura_status}`}>
                  <em>{evento.fuera_de_rango ? 'Temperatura (cruda)' : 'Temperatura'}</em>
                  <strong>{formatValue(evento.temperatura_do, 1)}<small>°C</small></strong>
                </span>
                <span className={`tx-alert-metric tx-cell-${evento.oxigeno_status}`}>
                  <em>Oxígeno</em>
                  <strong>{formatValue(evento.oxigeno_mgl, 1)}<small>mg/L</small></strong>
                </span>
                <span className={`tx-alert-motivo ${evento.fuera_de_rango ? 'is-crudo' : ''}`}>
                  {evento.motivo}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {rutinarios > 0 && (
        <p className="tx-panel-nota">
          Además, {formatCount(rutinarios)} {rutinarios === 1 ? 'aviso' : 'avisos'} del equipo
          {rutinarios === 1 ? ' que no desvía' : ' que no desvían'} temperatura ni oxígeno.
          {' '}Están en el detalle de mediciones y en el Excel.
        </p>
      )}
    </Panel>
  );
}

function RoomCard({ room, ops, onSelect, presentationMode }) {
  const hasDo = room.do_measurements > 0;
  const respaldo = presentationMode && Boolean(room.respaldo_nodered);
  // El estado lo calcula el servidor, que es también quien cuenta el semáforo de
  // la cabecera. Si aquí se recalculara con otra regla, el color de la tarjeta
  // podría contradecir el conteo de arriba.
  const status = room.status || 'neutral';
  const silent = status === 'sin_datos' && !respaldo;
  const bands = bandasDe(room);
  const temperatureStatus = statusFor(room.current_temperatura_do, bands.temperature);
  const oxygenStatus = statusFor(room.current_oxigeno_mgl, bands.oxygen);
  const saturationStatus = statusFor(room.current_saturacion_pct, bands.saturation);
  const statusReason = status === 'critical'
    ? (temperatureStatus === 'critical' ? 'Temperatura' : oxygenStatus === 'critical' ? 'Oxígeno disuelto' : saturationStatus === 'critical' ? 'Saturación' : null)
    : status === 'warning'
      ? (temperatureStatus === 'warning' ? 'Temperatura' : oxygenStatus === 'warning' ? 'Oxígeno disuelto' : saturationStatus === 'warning' ? 'Saturación' : null)
      : null;

  // El paneo general se concentra en las dos lecturas operativas principales.
  const readings = [
    (hasDo || respaldo) && { label: 'Temperatura', value: formatValue(room.current_temperatura_do), unit: '°C', status: temperatureStatus },
    hasDo && { label: 'Oxígeno disuelto', value: formatValue(room.current_oxigeno_mgl), unit: 'mg/L', status: oxygenStatus },
    respaldo && { label: 'Saturación', value: formatValue(room.current_saturacion_pct), unit: '%', status: saturationStatus },
  ].filter(Boolean).slice(0, 3);

  const lastSeen = room.last_seen_at || room.latest_at;

  return (
    <article className={`tx-room tx-room-${status}`}>
      <header>
        <div>
          {/* La base acompaña siempre al nombre. Se había quitado con una base
              fijada por no repetirla doce veces, pero «Sala 3» existe en más de
              una planta y quien lee la pantalla no siempre puso el filtro. */}
          <span className="tx-room-base">{baseLabel(room.base_code)}</span>
          <h3>{roomName(room)}</h3>
          {!respaldo && freshness(lastSeen, ops) !== 'ok' && (
            <span className={`tx-room-freshness tx-chip-${freshness(lastSeen, ops)}`}>
              {lastSeen ? `Última lectura ${relativeTime(lastSeen)}` : 'Nunca ha reportado'}
            </span>
          )}
        </div>
        <div className="tx-room-status">
          <StatusChip status={status} />
          {statusReason && <small className={`tx-room-status-reason tx-cell-${status}`}>Por {statusReason}</small>}
        </div>
      </header>

      {room.status === 'mantenimiento' ? (
        /* No es una sala muda ni una sala en rango: es una sala que alguien
           declaró intervenida, y sus lecturas de ese rato no describen el agua. */
        <p className="tx-room-silent tx-room-mantenimiento">
          En mantenimiento declarado. Sus lecturas no cuentan para el semáforo
          ni para los promedios mientras dure la ventana.
        </p>
      ) : silent ? (
        /* Una sala muda es el peor modo de falla: si no se dice, el tablero se
           ve verde por ausencia de datos malos, no porque esté bien. */
        <p className="tx-room-silent">
          {room.stale_minutes === null || room.stale_minutes === undefined
            ? 'Esta sala está en el catálogo pero no ha enviado ninguna lectura.'
            : `Sin lecturas desde hace ${formatDuration(room.stale_minutes)}.`}
          {room.reading_status && room.reading_status !== 'neutral' && (
            <> Última lectura conocida: <strong>{STATUS_LABEL[room.reading_status]}</strong>.</>
          )}
        </p>
      ) : (
        <div className="tx-room-readings">
          {readings.map((reading) => (
            <div key={reading.label} className={`tx-room-reading tx-cell-${reading.status}`}>
              <span>{reading.label}</span>
              <strong>{reading.value}<small>{reading.unit}</small></strong>
            </div>
          ))}
        </div>
      )}

      <footer>
        {!respaldo && <span>{formatCount(room.measurements)} mediciones</span>}
        <button type="button" className="tx-link-button" onClick={() => onSelect(room)}>
          Ver solo esta sala
        </button>
      </footer>
    </article>
  );
}

/* --------------------------------------------------------------------- */
/* Referencias: agua de mar y ambiente                                    */
/*                                                                        */
/* Las referencias tienen su propia banda: no son salas, no cuentan en el    */
/* semáforo y no pueden diluir el estado de las salas de cultivo.            */
/* --------------------------------------------------------------------- */

function ReferencesBand({ references, ops }) {
  if (!references?.length) return null;

  return (
    <Panel
      id="referencias"
      className="tx-references"
      titulo="Referencias"
      resumen="Puntos de contexto, fuera del semáforo de salas"
    >
      <div className="tx-reference-grid">
        {references.map((ref) => {
          const bandas = bandasDe(ref);
          const lecturas = [
            ref.current_sensor_temperature !== null && ref.current_sensor_temperature !== undefined && {
              label: 'Temperatura', value: formatValue(ref.current_sensor_temperature), unit: '°C',
              status: statusFor(ref.current_sensor_temperature, bandas.temperature),
            },
            ref.current_salinity_ppt !== null && ref.current_salinity_ppt !== undefined && {
              label: 'Salinidad', value: formatValue(ref.current_salinity_ppt, 1), unit: 'ppt',
              status: statusFor(ref.current_salinity_ppt, bandas.salinity),
            },
            /* El ambiente viaja en el mismo mensaje que el agua de mar, así que
               es una lectura más de esta serie y no una tarjeta aparte. Va sin
               semáforo: es contexto, y una nave más caliente que el agua no es
               una anomalía que haya que perseguir. */
            ref.current_ambient_temperature !== null && ref.current_ambient_temperature !== undefined && {
              label: 'Ambiente', value: formatValue(ref.current_ambient_temperature), unit: '°C',
              status: 'neutral',
            },
          ].filter(Boolean);

          return (
            <article key={ref.serie} className={`tx-reference tx-room-${ref.status}`}>
              <header>
                {/* Cada base tiene su propio punto de agua de mar y todos se
                    llaman igual. Sin la base, dos tarjetas idénticas dejan al
                    operador sin saber de qué planta es la lectura. */}
                <div>
                  <span className="tx-room-base">{baseLabel(ref.base_code)}</span>
                  <h4>{roomName(ref)}</h4>
                </div>
                <span className={`tx-room-freshness tx-chip-${freshness(ref.last_seen_at || ref.latest_at, ops)}`}>
                  {relativeTime(ref.last_seen_at || ref.latest_at)}
                </span>
              </header>
              <div className="tx-room-readings">
                {lecturas.map((lectura) => (
                  <div key={lectura.label} className={`tx-room-reading tx-cell-${lectura.status}`}>
                    <span>{lectura.label}</span>
                    <strong>{lectura.value}<small>{lectura.unit}</small></strong>
                  </div>
                ))}
              </div>
            </article>
          );
        })}
      </div>
    </Panel>
  );
}

function ChartTooltip({ active, payload, label, unit }) {
  if (!active || !payload?.length) return null;
  const items = payload
    .filter((entry) => entry.value !== null && entry.value !== undefined)
    .sort((a, b) => b.value - a.value);
  if (!items.length) return null;
  return (
    <div className="tx-tooltip">
      <p className="tx-tooltip-title">{formatDateTime(label)}</p>
      {items.map((entry) => (
        <p key={entry.dataKey} className="tx-tooltip-row">
          <span className="tx-tooltip-dot" style={{ background: entry.color }} />
          <span className="tx-tooltip-name">{entry.name}</span>
          <span className="tx-tooltip-value">{formatValue(entry.value)} {unit}</span>
        </p>
      ))}
    </div>
  );
}

function EvolutionChart({ chart, metric, threshold, hidden, onToggleSeries }) {
  const domain = useMemo(() => {
    const values = [];
    chart.data.forEach((row) => chart.meta.forEach((serie) => {
      const value = row[serie.key];
      if (value !== null && value !== undefined) values.push(value);
    }));
    if (!values.length) return null;
    const min = Math.min(...values);
    const max = Math.max(...values);
    const pad = (max - min) * 0.08 || Math.abs(max || 1) * 0.05;
    return { low: min - pad, high: max + pad };
  }, [chart]);

  if (!chart.data.length || !domain) return null;

  const band = threshold
    ? { y1: Math.max(threshold.idealMin, domain.low), y2: Math.min(threshold.idealMax, domain.high) }
    : null;

  return (
    <div className="tx-chart-frame">
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={chart.data} margin={{ top: 12, right: 24, left: 0, bottom: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.16)" />
        {band && band.y2 > band.y1 && (
          <ReferenceArea y1={band.y1} y2={band.y2} fill={metric.color} fillOpacity={0.07} strokeOpacity={0} />
        )}
        <XAxis
          dataKey="timestamp"
          type="number"
          scale="time"
          domain={['dataMin', 'dataMax']}
          tickFormatter={formatTimeAxis}
          stroke="#94a3b8"
          fontSize={11}
          minTickGap={40}
        />
        <YAxis
          stroke="#94a3b8"
          fontSize={11}
          width={54}
          domain={[Number(domain.low.toFixed(2)), Number(domain.high.toFixed(2))]}
          tickFormatter={(value) => formatValue(value, 1)}
        />
        <Tooltip content={<ChartTooltip unit={metric.unit} />} />
        <Legend
          onClick={(entry) => entry?.dataKey && onToggleSeries(entry.dataKey)}
          formatter={(value, entry) => {
            const serie = chart.meta.find((item) => item.key === entry?.dataKey);
            return (
              <span className={entry?.dataKey && hidden.has(entry.dataKey) ? 'tx-legend-off' : ''}>
                {serie?.shortLabel || value}
              </span>
            );
          }}
        />
        {chart.meta.map((serie) => (
          <Line
            key={serie.key}
            type="monotone"
            dataKey={serie.key}
            name={serie.label}
            stroke={serie.color}
            strokeWidth={2}
            dot={chart.data.length <= 90 ? { r: 2 } : false}
            activeDot={{ r: 5 }}
            connectNulls={false}
            hide={hidden.has(serie.key)}
            isAnimationActive={false}
          />
        ))}
        {chart.data.length > 60 && (
          <Brush
            dataKey="timestamp"
            height={22}
            travellerWidth={8}
            stroke="#06b6d4"
            fill="rgba(6,182,212,0.08)"
            tickFormatter={formatTimeAxis}
          />
        )}
      </LineChart>
    </ResponsiveContainer>
    </div>
  );
}

function Pagination({ pagination, onChange }) {
  const totalPages = Math.max(1, Math.ceil((pagination?.total || 0) / (pagination?.pageSize || 1)));
  if (!pagination || totalPages <= 1) return null;
  const page = pagination.page;
  return (
    <div className="tx-pagination">
      <span>
        {pagination.total.toLocaleString('es-EC')} registros · página {page} de {totalPages}
      </span>
      <div>
        <button type="button" disabled={page <= 1} onClick={() => onChange(1)}>« Primera</button>
        <button type="button" disabled={page <= 1} onClick={() => onChange(page - 1)}>Anterior</button>
        <button type="button" disabled={page >= totalPages} onClick={() => onChange(page + 1)}>Siguiente</button>
        <button type="button" disabled={page >= totalPages} onClick={() => onChange(totalPages)}>Última »</button>
      </div>
    </div>
  );
}

function MetricCell({ label, value, threshold }) {
  return (
    <td className={`tx-cell tx-cell-${statusFor(value, threshold)}`} data-label={label}>
      {formatValue(value)}
    </td>
  );
}

/* --------------------------------------------------------------------- */
/* Barra de filtros                                                       */
/*                                                                        */
/* La usan los dos bloques de la página con estado propio: monitoreo y    */
/* detalle filtran de forma independiente. No hay botón de aplicar; cada  */
/* cambio se propaga y la página lo consulta con un pequeño retraso.      */
/* --------------------------------------------------------------------- */

function FilterBar({
  id, label, value, bases, rooms,
  showStatus = false, presentationMode = false, onChange, onClear, extra, actions,
}) {
  const [forceDates, setForceDates] = useState(false);

  // Sin base elegida no hay lista de salas: el desplegable queda deshabilitado.
  const sinBase = !value.base || value.base === 'all';

  const visibleRooms = useMemo(
    () => (sinBase ? [] : rooms.filter((room) => room.base_code === value.base)),
    [sinBase, value.base, rooms]
  );

  // El atajo activo se guarda por identificador, no se deduce de las fechas: una
  // ventana móvil como «24 h» o «madrugada» no vuelve a coincidir con un rango
  // recalculado, y el botón quedaría apagado en cuanto se renderiza.
  const activeQuickRange = value.quick || null;

  // Un rango que no coincide con ningún atajo solo puede leerse en los campos
  // de fecha, así que se muestran sin que haya que pedirlo.
  const showDates = forceDates || activeQuickRange === null;

  // Tocar una fecha a mano deja de ser un atajo: se limpia `quick` para que el
  // botón no siga marcado sobre un rango que ya no es el suyo.
  const setDay = (field, day, endBoundary) => {
    if (!day) return;
    const next = clampRange(value, field, endBoundary ? endOfDay(day) : startOfDay(day));
    onChange({ ...next, quick: null });
  };

  const statusLabel = STATUS_FILTERS.find((item) => item.id === (value.status || 'all'))?.label;
  const statusOptions = presentationMode || value.base === 'all'
    ? STATUS_FILTERS.filter((item) => item.id !== 'sin_datos')
    : STATUS_FILTERS;

  // La sala elegida se identifica por su serie, que es sala + sensor juntos.
  const salaActiva = value.sala === 'all'
    ? null
    : visibleRooms.find((room) => String(room.sala) === String(value.sala)
        && String(room.sensor_num) === String(value.sensor));

  const chips = [
    value.base !== 'all' && { field: 'base', text: baseLabel(value.base) },
    value.sala !== 'all' && { field: 'sala', text: salaActiva ? roomShortName(salaActiva) : roomLabel(value.sala) },
    value.status && value.status !== 'all' && { field: 'status', text: statusLabel },
  ].filter(Boolean);

  return (
    <div className="tx-filters" role="group" aria-label={label}>
      <div className="tx-quick">
        {QUICK_RANGES.map((option) => (
          <button
            key={option.id}
            type="button"
            title={option.hint}
            className={activeQuickRange === option.id ? 'is-active' : ''}
            onClick={() => {
              setForceDates(false);
              onChange({ ...value, ...option.range(), quick: option.id });
            }}
          >
            {option.label}
          </button>
        ))}
        <button
          type="button"
          className={showDates ? 'is-active' : ''}
          onClick={() => setForceDates((current) => !current)}
        >
          Fechas
        </button>
      </div>

      <div className="tx-filters-fields">
        <label htmlFor={`${id}-base`}>
          Base
          <select
            id={`${id}-base`}
            value={value.base}
            onChange={(event) => onChange({ ...value, base: event.target.value, sala: 'all' })}
          >
            <option value="all">Todas las bases</option>
            {bases.map((base) => (
              <option key={base.code} value={base.code}>{baseLabel(base.code)} ({base.rooms})</option>
            ))}
          </select>
        </label>

        {/* Un solo desplegable para la sala real. Antes había dos —sala y
            sensor— sin relación entre sí, y para llegar a una sala concreta
            había que acertar con ambos; se podía elegir «TEX04-001 + Sensor 2»
            y obtener una pantalla vacía. Ahora cada opción es una sala de
            cultivo y envía el par completo.

            La sala se elige DESPUÉS de la base: la lista queda corta y no hace
            falta repetir el nombre de la base en cada opción, porque ya está
            fijada arriba. Con varias bases publicando, un desplegable con todas
            las salas de la finca sería inmanejable. */}
        <label htmlFor={`${id}-sala`}>
          Sala
          <select
            id={`${id}-sala`}
            disabled={sinBase}
            value={value.sala === 'all' ? 'all' : `${value.sala}#${value.sensor}`}
            onChange={(event) => {
              if (event.target.value === 'all') {
                onChange({ ...value, sala: 'all', sensor: 'all' });
                return;
              }
              const elegida = visibleRooms.find((room) => room.serie === event.target.value);
              if (!elegida) return;
              onChange({
                ...value,
                sala: elegida.sala,
                sensor: elegida.sensor_num === null || elegida.sensor_num === undefined
                  ? 'all'
                  : String(elegida.sensor_num),
              });
            }}
          >
            {sinBase ? (
              <option value="all">Elija una base primero</option>
            ) : (
              <>
                <option value="all">Todas las salas de la base</option>
                {visibleRooms.map((room) => (
                  <option key={room.serie} value={room.serie}>{roomOptionLabel(room)}</option>
                ))}
              </>
            )}
          </select>
          {sinBase && <em className="tx-filters-hint">La sala depende de la base</em>}
        </label>

        {/* Filtrar por estado es la entrada natural a la pantalla cuando hay
            muchas salas: lo primero que se pregunta es qué está mal. */}
        {showStatus && (
          <label htmlFor={`${id}-estado`}>
            Estado
            <select
              id={`${id}-estado`}
              value={value.status || 'all'}
              onChange={(event) => onChange({ ...value, status: event.target.value })}
            >
              {statusOptions.map((option) => (
                <option key={option.id} value={option.id}>{option.label}</option>
              ))}
            </select>
          </label>
        )}

        {showDates && (
          <>
            <label htmlFor={`${id}-desde`}>
              Desde
              <input
                id={`${id}-desde`}
                type="date"
                value={dayValue(value.from)}
                onChange={(event) => setDay('from', event.target.value, false)}
              />
            </label>
            <label htmlFor={`${id}-hasta`}>
              Hasta
              <input
                id={`${id}-hasta`}
                type="date"
                value={dayValue(value.to)}
                onChange={(event) => setDay('to', event.target.value, true)}
              />
            </label>
          </>
        )}

        {extra}
      </div>

      <div className="tx-filters-footer">
        {chips.length > 0 && (
          <div className="tx-active-filters">
            {chips.map((chip) => (
              <button
                key={chip.field}
                type="button"
                /* Quitar la sala tiene que soltar también el sensor: son las
                   dos mitades de un mismo filtro desde que la sala real es el
                   par. Quitar la base suelta ambas. */
                onClick={() => onChange({
                  ...value,
                  [chip.field]: 'all',
                  ...(chip.field === 'base' ? { sala: 'all', sensor: 'all' } : {}),
                  ...(chip.field === 'sala' ? { sensor: 'all' } : {}),
                })}
              >
                {chip.text} <em>×</em>
              </button>
            ))}
          </div>
        )}
        <div className="tx-filters-actions">
          {actions}
          <button className="tx-secondary" type="button" onClick={onClear}>Limpiar</button>
        </div>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------- */
/* Página                                                                 */
/* --------------------------------------------------------------------- */

export function TexcumarPage() {
  const presentationMode = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('demo') === '1' || params.get('presentacion') === '1';
  }, []);
  const initialFilters = useMemo(
    () => ({ ...rangeForDays(1), quick: 'today', base: 'all', sala: 'all', sensor: 'all', status: 'all' }),
    []
  );

  const [rooms, setRooms] = useState([]);
  const [bases, setBases] = useState([]);
  const [fleet, setFleet] = useState(null);
  const [references, setReferences] = useState([]);
  const [configOpen, setConfigOpen] = useState(false);
  const [mantenimientoOpen, setMantenimientoOpen] = useState(false);
  // Se incrementa al guardar la configuración, para releer con los umbrales
  // nuevos sin obligar a refrescar la página.
  const [reloadToken, setReloadToken] = useState(0);

  // Anclas de desplazamiento. Antes se resolvían con document.querySelector por
  // clase CSS, lo que ataba el comportamiento al nombre de una clase de estilo.
  const evolutionRef = useRef(null);

  const [monitorFilters, setMonitorFilters] = useState(initialFilters);

  const [stats, setStats] = useState(null);
  const [seriesRows, setSeriesRows] = useState([]);
  const [trends, setTrends] = useState([]);
  const [roomStatus, setRoomStatus] = useState([]);
  const [monitorLoading, setMonitorLoading] = useState(true);
  const [monitorError, setMonitorError] = useState(null);

  const [metric, setMetric] = useState('temperatura_do');
  const [hiddenSeries, setHiddenSeries] = useState(() => new Set());

  const [telemetry, setTelemetry] = useState([]);
  const [telemetryPagination, setTelemetryPagination] = useState({ page: 1, pageSize: 25, total: 0 });
  const [telemetryPage, setTelemetryPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [alerts, setAlerts] = useState(null);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(true);
  const [detailError, setDetailError] = useState(null);
  const [exporting, setExporting] = useState(false);

  /* La espera es solo para las fechas, que se teclean dígito a dígito. Base,
   * sala, sensor y los atajos de rango son clics: retrasarlos 350 ms hacía que
   * la pantalla pareciera pesada sin ahorrar ni una consulta.
   *
   * Y `status` ya no entra en la clave: se resuelve entero en el cliente, pero
   * al formar parte del JSON que se comparaba, pulsar «ver solo estas»
   * disparaba las cinco consultas otra vez para devolver exactamente lo mismo. */
  const fechasKey = useDebouncedValue(
    `${monitorFilters.from || ''}|${monitorFilters.to || ''}`,
    FILTER_DEBOUNCE_MS
  );

  const monitorKey = useMemo(() => {
    const [from, to] = fechasKey.split('|');
    return JSON.stringify({
      base: monitorFilters.base,
      sala: monitorFilters.sala,
      sensor: monitorFilters.sensor,
      from: from || undefined,
      to: to || undefined,
    });
  }, [monitorFilters.base, monitorFilters.sala, monitorFilters.sensor, fechasKey]);

  const monitorQuery = useMemo(() => JSON.parse(monitorKey), [monitorKey]);
  const detailQuery = monitorQuery;

  useEffect(() => {
    let cancelled = false;
    loadRooms()
      .then((result) => {
        if (cancelled) return;
        setRooms(result?.rooms || []);
        setBases(result?.bases || []);
      })
      .catch(() => {
        if (cancelled) return;
        setRooms([]);
        setBases([]);
      });
    return () => { cancelled = true; };
  }, [reloadToken]);

  /* Las cuatro consultas salen a la vez pero cada una pinta lo suyo en cuanto
   * llega, en vez de esperar a la más lenta.
   *
   * Medido contra producción con un día y tres bases: /fleet tarda ~1,0 s,
   * /series ~0,9 s con 350 KB, /stats y /trends ~0,5 s. Esperándolas todas, el
   * estado por sala aparecía cuando terminaba la última; ahora aparece con la
   * suya. Es la misma carga para el pool: el número de consultas no cambia. */
  useEffect(() => {
    let cancelled = false;
    setMonitorLoading(true);
    setMonitorError(null);

    const fallo = (err) => {
      if (!cancelled) setMonitorError(err.message || 'No se pudo cargar la telemetría.');
    };

    // /fleet trae el resumen de flota y el estado de cada sala en una sola
    // respuesta. Antes se pedía /by-room aparte; unirlos evita repetir la
    // consulta más costosa.
    const flota = getTexcumarFleet(monitorQuery)
      .then((fleetResult) => {
        if (cancelled) return;
        setFleet(fleetResult);
        setRoomStatus((fleetResult.data || []).map((room) => (
          presentationMode ? roomForPresentation(room) : room
        )));
        setReferences(fleetResult.referencias || []);
      })
      .catch(fallo);

    const resto = Promise.all([
      getTexcumarStats(monitorQuery).then((r) => { if (!cancelled) setStats(r); }),
      getTexcumarSeries({ ...monitorQuery, maxPoints: 600 })
        .then((r) => { if (!cancelled) setSeriesRows(r.data || []); }),
      getTexcumarTrends(monitorQuery).then((r) => { if (!cancelled) setTrends(r.data || []); }),
    ]).catch(fallo);

    Promise.all([flota, resto]).finally(() => {
      if (!cancelled) setMonitorLoading(false);
    });

    return () => { cancelled = true; };
  }, [monitorQuery, reloadToken, presentationMode]);

  /* La bandeja va en su propia petición y no dentro de /fleet: se pide con su
     * propio límite y no tiene por qué encarecer la consulta que pinta el
     * semáforo, que es la que bloquea la primera pintada. */
  useEffect(() => {
    let cancelled = false;
    setAlertsLoading(true);
    getTexcumarAlerts({ ...monitorQuery, limit: 12 })
      .then((result) => { if (!cancelled) setAlerts(result); })
      .catch(() => { if (!cancelled) setAlerts(null); })
      .finally(() => { if (!cancelled) setAlertsLoading(false); });
    return () => { cancelled = true; };
  }, [monitorQuery, reloadToken]);

  useEffect(() => {
    let cancelled = false;
    setDetailLoading(true);
    setDetailError(null);
    getTexcumarTelemetry({ ...detailQuery, page: telemetryPage, pageSize })
      .then((result) => {
        if (cancelled) return;
        setTelemetry(result.data || []);
        setTelemetryPagination(result.pagination);
      })
      .catch((err) => {
        if (!cancelled) setDetailError(err.message || 'No se pudo cargar el detalle de mediciones.');
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => { cancelled = true; };
  }, [detailQuery, telemetryPage, pageSize]);

  useEffect(() => { setTelemetryPage(1); }, [monitorKey, pageSize]);
  useEffect(() => { setHiddenSeries(new Set()); }, [metric, seriesRows]);

  const hasMetric = useCallback((item) => {
    const count = stats?.[item.countKey];
    if (count !== null && count !== undefined && Number(count) > 0) return true;
    if (trends.some((row) => row[`avg_${item.key}`] !== null && row[`avg_${item.key}`] !== undefined)) return true;
    return seriesRows.some((row) => row[item.key] !== null && row[item.key] !== undefined);
  }, [stats, trends, seriesRows]);

  const availableMetrics = useMemo(() => METRICS.filter(hasMetric), [hasMetric]);

  useEffect(() => {
    if (availableMetrics.length && !availableMetrics.some((item) => item.key === metric)) {
      setMetric(availableMetrics[0].key);
    }
  }, [availableMetrics, metric]);

  const activeMetric = useMemo(
    () => METRICS.find((item) => item.key === metric) || METRICS[0],
    [metric]
  );

  // El catálogo completo da nombre al detalle aunque el monitor esté filtrado;
  // el estado actual va después para superponer sus datos más recientes.
  const serieIndex = useMemo(() => {
    return new Map([...rooms, ...roomStatus, ...references].map((room) => [room.serie, room]));
  }, [rooms, roomStatus, references]);

  const serieDe = useCallback(
    (sala, sensor) => serieIndex.get(`${sala}#${sensor === null || sensor === undefined ? '' : sensor}`) || null,
    [serieIndex]
  );

  // Recorte por legibilidad: conserva las series más críticas y completa el
  // cupo con referencias u otras series presentes.
  const chartSelection = useMemo(() => limitEvolutionSeries(
    seriesRows,
    activeMetric.key,
    [...roomStatus].sort(byCriticality).map((room) => room.serie),
    MAX_CHART_SERIES
  ), [seriesRows, activeMetric, roomStatus]);
  const { rows: chartRows, total: chartSeriesTotal, hidden: seriesOcultas } = chartSelection;

  const chart = useMemo(
    () => buildEvolution(chartRows, activeMetric.key, (sala, sensor) => {
      const room = serieDe(sala, sensor);
      return room ? roomShortName(room) : null;
    }),
    [chartRows, activeMetric, serieDe]
  );

  const trendRange = useMemo(() => {
    const result = {};
    METRICS.forEach((item) => {
      let min = null;
      let max = null;
      trends.forEach((row) => {
        const low = row[`min_${item.key}`];
        const high = row[`max_${item.key}`];
        if (low !== null && low !== undefined) min = min === null ? low : Math.min(min, low);
        if (high !== null && high !== undefined) max = max === null ? high : Math.max(max, high);
      });
      result[item.key] = { min, max };
    });
    return result;
  }, [trends]);

  const scrollToEvolution = () => {
    evolutionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Se propaga también el sensor: sin él, «Ver solo esta sala» sobre una de las
  // dos salas de una unidad seguiría filtrando las dos.
  const focusRoom = (room) => {
    setMonitorFilters((current) => ({
      ...current,
      base: room.base_code,
      sala: room.sala,
      sensor: room.sensor_num === null || room.sensor_num === undefined ? 'all' : String(room.sensor_num),
      status: 'all',
    }));
    scrollToEvolution();
  };

  const focusMetric = (key) => {
    setMetric(key);
    scrollToEvolution();
  };

  // Un clic en el semáforo filtra por ese estado. Es el camino corto de «hay una
  // sala crítica» a «cuál es».
  const toggleSeries = (key) => {
    setHiddenSeries((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const exportExcel = async () => {
    setExporting(true);
    setDetailError(null);
    try {
      const result = await downloadTexcumarExport(detailQuery);
      const url = URL.createObjectURL(result.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = result.filename
        || `texcumar_${dayValue(detailQuery.from)}_a_${dayValue(detailQuery.to)}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setDetailError(err.message || 'No se pudo exportar el Excel.');
    } finally {
      setExporting(false);
    }
  };

  // Cuántas salas de cultivo quedan dentro del filtro. Filtrar la unidad
  // TEX04-002 sin elegir sensor deja DOS salas dentro, así que sigue siendo
  // modo flota: un valor puntual describiría solo una de ellas.
  const enAlcance = roomsInScope(roomStatus, monitorFilters.sala, monitorFilters.sensor);
  const fleetMode = isFleetView(enAlcance);

  // Bandas de la sala seleccionada, para las tarjetas KPI. Con varias salas
  // dentro no se muestran tarjetas, así que basta con la primera del alcance.
  const selectedBands = useMemo(() => {
    if (fleetMode) return BANDAS_CULTIVO;
    const elegida = roomStatus.find((room) => String(room.sala) === String(monitorFilters.sala)
      && String(room.sensor_num) === String(monitorFilters.sensor));
    return bandasDe(elegida);
  }, [fleetMode, roomStatus, monitorFilters.sala, monitorFilters.sensor]);

  const ops = fleet?.ops || DEFAULT_FRESHNESS;

  const statusFilter = monitorFilters.status || 'all';
  const baseSummaries = useMemo(() => summarizeBases(roomStatus), [roomStatus]);
  const visibleRooms = useMemo(() => {
    if (statusFilter === 'all') return roomStatus;
    if (statusFilter === 'problems') return roomStatus.filter((room) => isProblem(room.status));
    return roomStatus.filter((room) => room.status === statusFilter);
  }, [roomStatus, statusFilter]);

  const orderedRooms = useMemo(() => [...visibleRooms].sort(byCriticality), [visibleRooms]);

  const displayRooms = orderedRooms;
  const showRoomPanel = roomStatus.length > 0;
  /* Densidad inversa a la normalidad: lo que hay que atender va en tarjeta y
   * lo que está en rango se encoge a un azulejo. Con el tope, una avería
   * generalizada tampoco devuelve la pared de tarjetas que se quería evitar. */
  const salasAtencion = displayRooms.filter((room) => room.status !== 'ok').slice(0, ROOM_CARD_LIMIT);
  const salasMosaico = displayRooms.filter((room) => !salasAtencion.includes(room));
  const emptyReason = !seriesRows.length
    ? 'No hay lecturas en el rango seleccionado.'
    : `Las salas seleccionadas no reportan ${activeMetric.label.toLowerCase()}.`;

  const columns = {
    do: availableMetrics.some((item) => item.key === 'oxigeno_mgl'),
    sensorTemperature: availableMetrics.some((item) => item.key === 'sensor_temperature'),
    salinity: availableMetrics.some((item) => item.key === 'salinity_ppt'),
  };

  return (
    <main className="main-content texcumar-page">
      <header className="tx-header">
        <div className="tx-header-title">
          <span className="tx-eyebrow">Control distribuido · Texcumar</span>
          <h1>Operación consolidada</h1>
          <p className="tx-header-lead">
            {bases.length > 1
              ? `${bases.length} bases remotas, una sola vista para supervisar salas, tendencias y alertas.`
              : 'Una sola vista para supervisar salas, tendencias y alertas.'}
          </p>
        </div>
        <div className="tx-header-actions">
          <div className={`tx-pulse tx-pulse-${monitorLoading ? 'loading' : freshness(stats?.latest_at, ops)}`}>
            <span />
            <div>
              <strong>{monitorLoading ? 'Actualizando…' : relativeTime(stats?.latest_at)}</strong>
              <em>{stats?.latest_at ? formatDateTime(stats.latest_at) : 'Sin lecturas en el rango'}</em>
            </div>
          </div>
          <button type="button" className="tx-secondary" onClick={() => setConfigOpen(true)}>
            Configuración
          </button>
        </div>
      </header>

      {presentationMode && (
        <div className="tx-presentation" role="status">
          <span />
          <div><strong>Modo presentación</strong><small>Escenario demostrativo de la operación consolidada</small></div>
        </div>
      )}

      {/* Nombres, umbrales y calibración por sala. Al guardar se recargan los
          datos para que el cambio se vea sin refrescar la página a mano. */}
      <MantenimientoPanel
        isOpen={mantenimientoOpen}
        onClose={() => setMantenimientoOpen(false)}
        rooms={roomStatus}
        seleccionInicial={monitorFilters.sala !== 'all' && monitorFilters.sensor !== 'all'
          ? `${monitorFilters.sala}#${monitorFilters.sensor}`
          : null}
        onSaved={() => setReloadToken((n) => n + 1)}
      />

      <TexcumarConfigPanel
        isOpen={configOpen}
        onClose={() => setConfigOpen(false)}
        rooms={[...roomStatus, ...references]}
        onSaved={() => { roomsPromise = null; setReloadToken((n) => n + 1); }}
      />

      {/* ---------------- Bloque 1: monitoreo ---------------- */}
      <section className="tx-block tx-block-monitor">
        <div className="tx-block-header">
          <div>
            <span className="tx-block-tag">Monitoreo</span>
            <h2>Supervisión y análisis</h2>
            <p>
              {rangeLabel(monitorFilters.from, monitorFilters.to)}
              {fleetMode && fleet?.counts?.total ? (
                <> · {fleet.bases} {fleet.bases === 1 ? 'base' : 'bases'} · {fleet.counts.total} salas</>
              ) : null}
            </p>
          </div>
          <div className="tx-block-count">
            <strong>{formatCount(stats?.measurements ?? 0)}</strong>
            <em>mediciones</em>
          </div>
        </div>

        {monitorError && <div className="tx-error">{monitorError}</div>}

        <FleetSummary
          fleet={fleet}
          bases={baseSummaries}
          activeBase={monitorFilters.base}
          onSelectBase={(base) => setMonitorFilters((current) => ({
            ...current, base, sala: 'all', sensor: 'all', status: 'all',
          }))}
          onFocusRoom={focusRoom}
          onFilterStatus={(status) => setMonitorFilters((current) => ({ ...current, status }))}
        />

        <FilterBar
          id="monitor"
          label="Filtros de monitoreo"
          value={monitorFilters}
          bases={bases}
          rooms={rooms}
          showStatus={showRoomPanel}
          presentationMode={presentationMode}
          onChange={setMonitorFilters}
          onClear={() => setMonitorFilters(initialFilters)}
          actions={monitorFilters.base !== 'all' && (
            <button
              className="tx-secondary"
              type="button"
              onClick={() => setMonitorFilters((current) => ({
                ...current, base: 'all', sala: 'all', sensor: 'all', status: 'problems',
              }))}
            >
              Ver consolidado
            </button>
          )}
        />

        {/* Con «todas las bases / todas las salas» un valor puntual no
            representa a la flota: puede marcar Óptimo mientras otra sala está
            en hipoxia. Por eso en modo flota la pantalla abre directamente con
            las tarjetas de sala, que es donde el dato tiene dueño. Al fijar una
            sala, el valor puntual vuelve a tener sentido y aparecen las
            tarjetas con Mín/Prom/Máx. */}
        {!fleetMode && (monitorLoading && !stats ? (
          <div className="tx-kpis">
            {[0, 1, 2, 3].map((index) => <div key={index} className="tx-kpi tx-skeleton" />)}
          </div>
        ) : (
          <div className="tx-kpis">
            {availableMetrics.map((item) => (
              <KpiCard
                key={item.key}
                metric={item}
                stats={stats}
                range={trendRange[item.key]}
                threshold={item.threshold ? selectedBands[item.threshold] : null}
                onFocus={focusMetric}
              />
            ))}
          </div>
        ))}

        {/* En flota, las tarjetas de sala son lo primero que se ve, así que la
            espera se rellena con su propia silueta y no con la de otro bloque. */}
        {fleetMode && monitorLoading && !roomStatus.length && (
          <div className="tx-rooms-grid">
            {[0, 1, 2, 3, 4].map((index) => <div key={index} className="tx-room tx-skeleton" />)}
          </div>
        )}

        {showRoomPanel && (
          <Panel
            id="salas"
            titulo="Estado por sala"
            resumen={statusFilter === 'problems'
              ? `${displayRooms.length} ${displayRooms.length === 1 ? 'sala requiere' : 'salas requieren'} atención`
              : `${displayRooms.length} de ${roomStatus.length} salas`}
            acciones={(
              <div className="tx-panel-acciones">
                {statusFilter !== 'all' && (
                  <button
                    type="button"
                    className="tx-secondary"
                    onClick={() => setMonitorFilters((current) => ({ ...current, status: 'all' }))}
                  >
                    Ver todas
                  </button>
                )}
                {/* Vive aquí y no en cada tarjeta: quien limpia suele hacerlo en
                    varias salas seguidas, y el diálogo admite elegir todas de
                    una vez. */}
                <button
                  type="button"
                  className="tx-secondary"
                  onClick={() => setMantenimientoOpen(true)}
                >
                  Registrar mantenimiento
                </button>
              </div>
            )}
          >
            <div className="tx-leyenda">
              <span className="tx-cell-ok">■ Verde: dentro del rango ideal</span>
              <span className="tx-cell-warning">■ Ámbar: fuera del ideal, antes de que salte la alarma</span>
              <span className="tx-cell-critical">■ Rojo: fuera del rango operativo</span>
              {!presentationMode && <span className="tx-cell-sin_datos">■ Gris: sin lecturas recientes</span>}
              <span className="tx-cell-mantenimiento">■ Azul: mantenimiento declarado</span>
            </div>

            {displayRooms.length === 0 ? (
              <div className="tx-empty">
                {statusFilter === 'problems' ? 'Todas las salas reportan y están dentro de rango.' : 'Ninguna sala coincide con el estado seleccionado.'}
              </div>
            ) : (
              <>
                {salasAtencion.length > 0 && (
                  <div className="tx-rooms-grid">
                    {salasAtencion.map((room) => (
                      /* La clave es la serie: dos salas de la misma unidad
                         comparten `sala` y React las mezclaría. */
                      <RoomCard
                        key={room.serie}
                        room={room}
                        ops={ops}
                        onSelect={focusRoom}
                        presentationMode={presentationMode}
                      />
                    ))}
                  </div>
                )}
                {salasMosaico.length > 0 && (
                  <div className="tx-mosaic">
                    {salasMosaico.map((room) => (
                      <RoomTile key={room.serie} room={room} onSelect={focusRoom} />
                    ))}
                  </div>
                )}
              </>
            )}
          </Panel>
        )}

        <AlertsTray
          alerts={alerts}
          loading={alertsLoading}
          onSelectRoom={focusRoom}
        />

        <ReferencesBand references={references} ops={ops} />

        <Panel
          id="evolucion"
          className="tx-evolution"
          ref={evolutionRef}
          titulo="Evolución en el tiempo"
          resumen={`${chart.meta.length} ${chart.meta.length === 1 ? 'serie' : 'series'}`
            + `${seriesOcultas > 0 ? ` de ${chartSeriesTotal} (las más críticas)` : ''}`
            + `${chart.bucket ? ` · promedio cada ${bucketLabel(chart.bucket)}` : ''}`}
          acciones={(
            <div className="tx-metric-tabs">
              {availableMetrics.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className={item.key === activeMetric.key ? 'is-active' : ''}
                  onClick={() => setMetric(item.key)}
                  style={item.key === activeMetric.key ? { borderColor: item.color, color: item.color } : undefined}
                >
                  {item.short}
                </button>
              ))}
            </div>
          )}
        >
          {chart.data.length ? (
            <EvolutionChart
              chart={chart}
              metric={activeMetric}
              threshold={activeMetric.threshold ? selectedBands[activeMetric.threshold] : null}
              hidden={hiddenSeries}
              onToggleSeries={toggleSeries}
            />
          ) : (
            <div className="tx-empty">{monitorLoading ? 'Cargando mediciones…' : emptyReason}</div>
          )}
        </Panel>

      </section>

      {/* ---------------- Bloque 2: detalle ---------------- */}
      <section className="tx-block tx-block-detail">
        <div className="tx-block-header">
          <div>
            <span className="tx-block-tag">Detalle</span>
            <h2>Detalle de mediciones</h2>
            <p>{rangeLabel(monitorFilters.from, monitorFilters.to)} · usa el filtro superior</p>
          </div>
          <div className="tx-block-count">
            <strong>{(telemetryPagination.total || 0).toLocaleString('es-EC')}</strong>
            <em>registros</em>
          </div>
        </div>

        <div className="tx-detail-summary">
          <p>
            Se exportarán <strong>{(telemetryPagination.total || 0).toLocaleString('es-EC')}</strong> registros
            {' '}de {rangeLabel(monitorFilters.from, monitorFilters.to)}
          </p>
          <label htmlFor="detalle-filas">
            Filas
            <select
              id="detalle-filas"
              value={pageSize}
              onChange={(event) => setPageSize(Number(event.target.value))}
            >
              {PAGE_SIZES.map((size) => <option key={size} value={size}>{size}</option>)}
            </select>
          </label>
          <button
            className="tx-export"
            type="button"
            onClick={exportExcel}
            disabled={exporting || !telemetryPagination.total}
          >
            {exporting ? 'Generando…' : 'Descargar Excel'}
          </button>
        </div>

        {detailError && <div className="tx-error">{detailError}</div>}

        <div className="tx-table-scroll">
          <table>
            <thead>
              <tr>
                <th>Fecha/Hora</th>
                <th>Sala</th>
                {columns.do && <><th>Temperatura °C</th><th>Oxígeno mg/L</th><th>Saturación %</th></>}
                {columns.sensorTemperature && <th>Agua de mar °C</th>}
                {columns.salinity && <th>Salinidad ppt</th>}
                <th>Tipo de evento</th>
              </tr>
            </thead>
            <tbody>
              {telemetry.map((row) => {
                /* Estas filas llegan calibradas, igual que el panel de arriba
                   y que el Excel: un mismo valor no puede aparecer con dos
                   números en la misma pantalla. Lo que /telemetry no aplica es
                   el saneo, así que aquí sí se ven las lecturas físicamente
                   imposibles que los promedios descartan. */
                const room = serieDe(row.sala, row.sensor_num);
                const bandas = bandasDe(room);
                return (
                <tr key={`${row.id}-${row.message_id}`}>
                  <td data-label="Fecha/Hora">{formatDateTime(row.telemetry_time)}</td>
                  <td data-label="Sala">
                    {room ? roomShortName(room) : roomLabel(row.sala)}
                  </td>
                  {columns.do && (
                    <>
                      <MetricCell label="Temperatura °C" value={row.temperatura_do} threshold={null} />
                      <MetricCell label="Oxígeno mg/L" value={row.oxigeno_mgl} threshold={null} />
                      <MetricCell label="Saturación %" value={row.saturacion_pct} threshold={bandas.saturation} />
                    </>
                  )}
                  {columns.sensorTemperature && (
                    <MetricCell label="Agua de mar °C" value={row.sensor_temperature} threshold={null} />
                  )}
                  {columns.salinity && (
                    <MetricCell label="Salinidad ppt" value={row.salinity_ppt} threshold={null} />
                  )}
                  <td data-label="Tipo de evento"><span className="tx-event">{row.event_type || '—'}</span></td>
                </tr>
                );
              })}
            </tbody>
          </table>
          {!telemetry.length && (
            <div className="tx-empty">
              {detailLoading ? 'Cargando mediciones…' : 'No hay mediciones con estos filtros.'}
            </div>
          )}
        </div>
        <Pagination pagination={telemetryPagination} onChange={setTelemetryPage} />
      </section>
    </main>
  );
}
